import type {
  DisplayFormat,
  DisplayPosition,
  ProfessionHolderInput,
  ProfessionHolderPatch,
  ProfessionRuleInput,
  ProfessionRulePatch,
  TenantProfessionCreate,
  TenantProfessionInput,
} from "@juro/shared";
import { getAuthToken } from "../lib/clerkAuth.js";

const DEV_USER_ID = "user_amano_akihiro";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = await getAuthToken();
  const res = await fetch(`/api${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      "X-User-Id": DEV_USER_ID,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = (body as { error?: { message?: string } }).error?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export type ProfessionRule = {
  id: string;
  profession: string;
  field_key: string;
  label: string;
  value: string | null;
  applies_to_doc_types: string[] | null;
  display_position: DisplayPosition;
  display_format: DisplayFormat;
  display_order: number;
  is_builtin: boolean;
  created_at?: string;
  updated_at?: string;
};

/** 資格者名簿1件（tenant_profession_holders）。氏名・士業登録番号・適格請求書発行事業者番号を直接持つ */
export type ProfessionHolder = {
  id: string;
  profession: string;
  name: string;
  professional_registration_number: string | null;
  invoice_registration_number: string | null;
  is_default: boolean;
  display_order: number;
};

/**
 * 資格マスタ1件（組み込み3資格＋カスタム資格を1系統で扱う。Phase 1 統合後）。
 * `is_builtin` が true の場合（司法書士・行政書士・土地家屋調査士）は
 * 削除不可・`profession` 値変更不可（API・UI 両方でブロックする）。
 *
 * 資格者（誰の氏名・登録番号で発行するか）は資格種別ごとのフラットな名簿
 * （tenant_profession_holders）を唯一の情報源とする
 * （docs/design/user-professional-separation.md）。
 */
export type TenantProfession = {
  id: string;
  tenant_id: string;
  profession: string;
  qualification_label: string;
  /** tenant_profession_holders.id（この資格種別の既定資格者） */
  holder_id: string | null;
  display_order: number;
  is_builtin: boolean;
  updated_at: string;
  holder: {
    id: string;
    name: string;
    registration_number: string | null;
    invoice_registration_number: string | null;
  } | null;
  rules: ProfessionRule[];
};

export const tenantProfessionsApi = {
  list: () => request<{ data: TenantProfession[]; total: number }>("/tenant-professions"),
  /** 新規資格の作成（`profession` 省略時はサーバー側でラベルから生成する） */
  create: (body: TenantProfessionCreate) =>
    request<TenantProfession>("/tenant-professions", { method: "POST", body: JSON.stringify(body) }),
  updateHead: (profession: string, body: TenantProfessionInput) =>
    request<TenantProfession>(`/tenant-professions/${profession}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  /** 論理削除。組み込み資格（is_builtin）は 422 で拒否される */
  remove: (profession: string) =>
    request<{ ok: boolean }>(`/tenant-professions/${profession}`, { method: "DELETE" }),
  createRule: (profession: string, body: ProfessionRuleInput) =>
    request<ProfessionRule>(`/tenant-professions/${profession}/rules`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateRule: (profession: string, ruleId: string, body: ProfessionRulePatch) =>
    request<ProfessionRule>(`/tenant-professions/${profession}/rules/${ruleId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  deleteRule: (profession: string, ruleId: string) =>
    request<{ ok: boolean }>(`/tenant-professions/${profession}/rules/${ruleId}`, {
      method: "DELETE",
    }),
  listHolders: (profession: string) =>
    request<{ data: ProfessionHolder[]; total: number }>(`/tenant-professions/${profession}/holders`),
  addHolder: (profession: string, body: ProfessionHolderInput) =>
    request<ProfessionHolder>(`/tenant-professions/${profession}/holders`, {
      method: "POST",
      body: JSON.stringify(body),
    }),
  updateHolder: (profession: string, holderId: string, body: ProfessionHolderPatch) =>
    request<ProfessionHolder>(`/tenant-professions/${profession}/holders/${holderId}`, {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  removeHolder: (profession: string, holderId: string) =>
    request<{ ok: boolean }>(`/tenant-professions/${profession}/holders/${holderId}`, {
      method: "DELETE",
    }),
};
